import { useSubpantalla } from '../ui/navegacionReact';
import { useEffect, useState } from 'react';
import {
  Headset, Loader2, ChevronLeft, MessageSquare, Lock, Clock,
  CheckCircle2, PauseCircle, UserPlus, HandMetal, ArrowUpRight, BookOpen,
} from 'lucide-react';
import { api, ErrorApi } from '../api';
import Ayuda from '../ui/Ayuda';
import EstadoVacio from '../ui/EstadoVacio';
import TiempoRelativo from '../ui/TiempoRelativo';
import EscalarATego from './EscalarATego';

/* =====================================================================
   Mesa de ayuda · DIRECTRIZ-TICKETS F3
   =====================================================================
   La cola de quien atiende: filtra (míos, sin asignar, por vencer,
   vencidos, todos), abre un ticket, lo toma, lo asigna, responde, deja
   notas internas, lo pone en espera con motivo y lo marca resuelto con su
   solución. En el teléfono: tarjetas y botones grandes (invariantes M2/M4).
   ===================================================================== */

const ESTADO = {
  NUEVO: { texto: 'Nuevo', clase: 'bg-blue-100 text-blue-800' },
  ASIGNADO: { texto: 'Asignado', clase: 'bg-blue-100 text-blue-800' },
  EN_PROCESO: { texto: 'En proceso', clase: 'bg-blue-100 text-blue-800' },
  EN_ESPERA: { texto: 'En espera', clase: 'bg-amber-100 text-amber-800' },
  RESUELTO: { texto: 'Resuelto', clase: 'bg-green-100 text-green-800' },
  CERRADO: { texto: 'Cerrado', clase: 'bg-gray-200 text-gray-700' },
  REABIERTO: { texto: 'Reabierto', clase: 'bg-amber-100 text-amber-800' },
};

const PRIORIDAD = {
  1: { texto: 'Crítica', clase: 'bg-red-100 text-red-800' },
  2: { texto: 'Alta', clase: 'bg-orange-100 text-orange-800' },
  3: { texto: 'Media', clase: 'bg-yellow-100 text-yellow-800' },
  4: { texto: 'Baja', clase: 'bg-gray-100 text-gray-700' },
};

const FILTROS = [
  { clave: 'mio', etiqueta: 'Míos' },
  { clave: 'sin_asignar', etiqueta: 'Sin asignar' },
  { clave: 'por_vencer', etiqueta: 'Por vencer' },
  { clave: 'vencido', etiqueta: 'Vencidos' },
  { clave: 'todos', etiqueta: 'Todos' },
];

function Chip({ mapa, valor }) {
  const e = mapa[valor] || { texto: String(valor), clase: 'bg-gray-200 text-gray-700' };
  return <span className={`inline-block rounded px-2 py-0.5 text-xs font-semibold ${e.clase}`}>{e.texto}</span>;
}

// El reloj en palabras y color: verde=con tiempo, amarillo=por vencer,
// rojo=vencido, gris=en espera. Consistencia de colores de estado.
function humanoMin(min) {
  const abs = Math.abs(min);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  if (h > 24) return `${Math.floor(h / 24)} d`;
  if (h > 0) return `${h} h ${m} min`;
  return `${m} min`;
}

function Reloj({ reloj }) {
  if (!reloj || !reloj.con_sla) return null;
  if (reloj.en_espera) {
    return <span className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-semibold bg-gray-200 text-gray-700"><PauseCircle className="h-3 w-3" aria-hidden="true" /> En espera</span>;
  }
  if (reloj.vencido) {
    return <span className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-semibold bg-red-100 text-red-800"><Clock className="h-3 w-3" aria-hidden="true" /> Vencido hace {humanoMin(reloj.restante_min)}</span>;
  }
  const clase = reloj.por_vencer ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800';
  return <span className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-semibold ${clase}`}><Clock className="h-3 w-3" aria-hidden="true" /> Quedan {humanoMin(reloj.restante_min)}</span>;
}

export default function MesaDeAyuda() {
  const [filtro, setFiltro] = useState('mio');
  const [lista, setLista] = useState(null);
  const [error, setError] = useState('');
  const [abierto, setAbierto] = useState(null);
  useSubpantalla('ticket', !!abierto, () => setAbierto(null), { valor: abierto, abrir: setAbierto });

  function recargar() {
    api.get(`/tickets/mesa?filtro=${filtro}`)
      .then((l) => { setLista(l); setError(''); })
      .catch((e) => setError(e instanceof ErrorApi ? e.message : 'No se pudo cargar la cola.'));
  }
  useEffect(recargar, [filtro]);

  if (abierto) {
    return <Detalle id={abierto} onVolver={() => { setAbierto(null); recargar(); }} />;
  }

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-4 p-4">
      <header>
        <h1 className="flex items-center gap-2 text-xl font-semibold text-gray-900">
          <Headset className="h-6 w-6" aria-hidden="true" /> Mesa de ayuda
        </h1>
        <p className="text-gray-600">Los tickets que te toca atender. Ábrelos para responder y resolver.</p>
        <Ayuda id="mesa.pantalla" />
      </header>

      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filtros de la cola">
        {FILTROS.map((f) => (
          <button
            key={f.clave}
            type="button"
            role="tab"
            aria-selected={filtro === f.clave}
            onClick={() => { setLista(null); setFiltro(f.clave); }}
            className={`min-h-[44px] rounded-full px-4 text-sm font-semibold ${
              filtro === f.clave
                ? 'bg-[var(--color-marca)] text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {f.etiqueta}
          </button>
        ))}
      </div>

      {error && <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-800">{error}</p>}

      {lista === null && !error && (
        <p className="flex items-center gap-2 py-8 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Cargando la cola…
        </p>
      )}

      {lista !== null && lista.length === 0 && (
        <EstadoVacio
          icono={Headset}
          titulo="No hay tickets en este filtro"
          descripcion="Cuando llegue un ticket que te toque atender, aparecerá aquí. Prueba el filtro «Todos» para ver la cola completa."
        />
      )}

      {lista !== null && lista.length > 0 && (
        <ul className="flex flex-col gap-2">
          {lista.map((t) => (
            <li key={t.id}>
              <button
                type="button"
                onClick={() => setAbierto(t.id)}
                className="flex w-full flex-col gap-1 rounded-xl border border-gray-200 p-3 text-left hover:bg-gray-50"
              >
                <span className="flex items-center justify-between gap-2">
                  <span className="min-w-0 truncate font-medium text-gray-900">{t.titulo}</span>
                  <Chip mapa={PRIORIDAD} valor={t.prioridad} />
                </span>
                <span className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                  <span>{t.folio}</span>
                  <Chip mapa={ESTADO} valor={t.estado} />
                  <Reloj reloj={t.reloj} />
                  {t.asignado_a == null && <span className="text-gray-400">Sin asignar</span>}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// El detalle: conversación, notas internas y las acciones de atención
// ---------------------------------------------------------------------
function Detalle({ id, onVolver }) {
  const [t, setT] = useState(null);
  const [error, setError] = useState('');
  const [tecnicos, setTecnicos] = useState([]);
  const [mensaje, setMensaje] = useState('');
  const [interno, setInterno] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [escalando, setEscalando] = useState(false);
  const [escalado, setEscalado] = useState(null);   // lo que ya se mandó a TEGO

  function recargar() {
    api.get(`/tickets/${id}`)
      .then((d) => { setT(d); setError(''); })
      .catch((e) => setError(e instanceof ErrorApi ? e.message : 'No se pudo abrir el ticket.'));
    api.get(`/tickets/${id}/escalado`).then(setEscalado).catch(() => setEscalado(null));
  }
  useEffect(recargar, [id]);
  useEffect(() => {
    api.get('/tickets/tecnicos').then(setTecnicos).catch(() => setTecnicos([]));
  }, []);

  async function accion(fn) {
    if (ocupado) return;
    setOcupado(true);
    try { await fn(); recargar(); }
    catch (e) { setError(e instanceof ErrorApi ? e.message : 'No se pudo completar la acción.'); }
    finally { setOcupado(false); }
  }

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-4 p-4">
      <button type="button" onClick={onVolver}
              className="flex items-center gap-1 self-start text-sm text-gray-600 hover:text-gray-900">
        <ChevronLeft className="h-4 w-4" aria-hidden="true" /> Volver a la cola
      </button>

      {error && <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-800">{error}</p>}
      {!t && !error && (
        <p className="flex items-center gap-2 py-8 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Abriendo el ticket…
        </p>
      )}

      {t && (
        <>
          <header className="flex flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-lg font-semibold text-gray-900">{t.titulo}</h1>
              <Chip mapa={ESTADO} valor={t.estado} />
              <Chip mapa={PRIORIDAD} valor={t.prioridad} />
            </div>
            <p className="text-xs text-gray-500">{t.folio} · {t.categoria}</p>
            <Ayuda id="mesa.reloj" />
          </header>

          {t.descripcion && (
            <p className="rounded-lg bg-gray-50 p-3 text-sm text-gray-800">{t.descripcion}</p>
          )}

          <Acciones t={t} tecnicos={tecnicos} ocupado={ocupado} accion={accion} id={id} />

          {(t.estado === 'RESUELTO' || t.estado === 'CERRADO') && (
            <GuardarComoArticulo id={id} />
          )}

          {t.categoria === 'Problema con TEGO' && (
            escalado ? (
              <div className="rounded-xl border border-green-200 bg-green-50 p-3">
                <p className="flex items-center gap-2 text-sm font-medium text-green-900">
                  <ArrowUpRight className="h-4 w-4" aria-hidden="true" /> Escalado al soporte de TEGO
                </p>
                <p className="mt-1 text-sm text-green-800">
                  Referencia <span className="font-semibold">{escalado.referencia}</span>.
                  El soporte de TEGO ve solo lo que se envió aquí.
                </p>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setEscalando(true)}
                className="flex min-h-[48px] items-center justify-center gap-2 self-start
                           rounded-xl border border-[var(--color-marca)] px-4 font-semibold
                           text-[var(--color-marca)] hover:bg-blue-50"
              >
                <ArrowUpRight className="h-5 w-5" aria-hidden="true" /> Escalar a TEGO
              </button>
            )
          )}

          <section>
            <h2 className="mb-2 flex items-center gap-2 font-medium text-gray-900">
              <MessageSquare className="h-4 w-4" aria-hidden="true" /> Conversación
            </h2>
            <ul className="flex flex-col gap-2">
              {t.eventos.filter((e) => e.mensaje).map((e) => (
                <li key={e.id}
                    className={`rounded-lg p-2 text-sm ${e.interno ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50'}`}>
                  {e.interno && (
                    <span className="mr-1 inline-flex items-center gap-1 text-xs font-semibold text-amber-700">
                      <Lock className="h-3 w-3" aria-hidden="true" /> Nota interna
                    </span>
                  )}
                  <span className="text-gray-800">{e.mensaje}</span>
                  <span className="ml-2 text-xs text-gray-400"><TiempoRelativo valor={e.creado_en} /></span>
                </li>
              ))}
            </ul>
          </section>

          {t.estado !== 'CERRADO' && (
            <div className="flex flex-col gap-2 rounded-xl border border-gray-200 p-3">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={interno} onChange={(e) => setInterno(e.target.checked)}
                       className="h-5 w-5" />
                Nota interna (solo la ve el equipo, no quien reportó)
              </label>
              <textarea
                value={mensaje} onChange={(e) => setMensaje(e.target.value)} rows={2}
                placeholder={interno ? 'Escribe una nota para el equipo…' : 'Escribe una respuesta para quien reportó…'}
                className="w-full rounded-xl border border-gray-300 p-3 text-base focus:border-[var(--color-marca)] focus:outline-none focus:ring-2 focus:ring-[var(--color-marca)]"
              />
              <button
                type="button"
                disabled={!mensaje.trim() || ocupado}
                onClick={() => accion(async () => {
                  await api.post(`/tickets/${id}/eventos`, {
                    mensaje: mensaje.trim(), tipo: interno ? 'NOTA_INTERNA' : 'MENSAJE',
                  });
                  setMensaje('');
                })}
                className="min-h-[48px] rounded-xl bg-[var(--color-marca)] px-4 font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                {interno ? 'Guardar nota interna' : 'Enviar respuesta'}
              </button>
            </div>
          )}

          {escalando && (
            <EscalarATego
              idTicket={id}
              onCerrar={() => setEscalando(false)}
              onEscalado={() => recargar()}
            />
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// Los botones de atención: tomar, asignar, poner en espera, resolver
// ---------------------------------------------------------------------
function Acciones({ t, tecnicos, ocupado, accion, id }) {
  const [motivo, setMotivo] = useState('');
  const [aQuien, setAQuien] = useState('');
  const [espera, setEspera] = useState('SOLICITANTE');
  const [solucion, setSolucion] = useState('');
  const [causa, setCausa] = useState('');
  const [tiempo, setTiempo] = useState('');
  const [panel, setPanel] = useState('');   // '', 'asignar', 'espera', 'resolver'
  useSubpantalla('panel', panel !== '', () => setPanel(''), { valor: panel, abrir: setPanel, reemplaza: true });
  const cerrado = t.estado === 'RESUELTO' || t.estado === 'CERRADO';

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-2">
        {!cerrado && (
          <button type="button" disabled={ocupado}
                  onClick={() => accion(() => api.post(`/tickets/${id}/tomar`, {}))}
                  className="flex min-h-[48px] items-center gap-2 rounded-xl bg-[var(--color-marca)] px-4 font-semibold text-white disabled:opacity-50">
            <HandMetal className="h-5 w-5" aria-hidden="true" /> Tomar
          </button>
        )}
        {!cerrado && (
          <button type="button"
                  onClick={() => setPanel(panel === 'asignar' ? '' : 'asignar')}
                  className="flex min-h-[48px] items-center gap-2 rounded-xl border border-gray-300 px-4 font-semibold text-gray-800 hover:bg-gray-50">
            <UserPlus className="h-5 w-5" aria-hidden="true" /> Asignar
          </button>
        )}
        {!cerrado && (
          <button type="button"
                  onClick={() => setPanel(panel === 'espera' ? '' : 'espera')}
                  className="flex min-h-[48px] items-center gap-2 rounded-xl border border-gray-300 px-4 font-semibold text-gray-800 hover:bg-gray-50">
            <PauseCircle className="h-5 w-5" aria-hidden="true" /> Poner en espera
          </button>
        )}
        {!cerrado && (
          <button type="button"
                  onClick={() => setPanel(panel === 'resolver' ? '' : 'resolver')}
                  className="flex min-h-[48px] items-center gap-2 rounded-xl bg-green-600 px-4 font-semibold text-white">
            <CheckCircle2 className="h-5 w-5" aria-hidden="true" /> Marcar resuelto
          </button>
        )}
      </div>

      {panel === 'asignar' && (
        <div className="flex flex-col gap-2 rounded-xl border border-gray-200 p-3">
          <Ayuda id="mesa.asignar" />
          <select value={aQuien} onChange={(e) => setAQuien(e.target.value)}
                  className="min-h-[48px] rounded-xl border border-gray-300 px-3 text-base">
            <option value="">Elige a quién se lo pasas…</option>
            {tecnicos.map((u) => <option key={u.id} value={u.id}>{u.nombre_completo}</option>)}
          </select>
          <input value={motivo} onChange={(e) => setMotivo(e.target.value)}
                 placeholder="Por qué se lo pasas (queda en el historial)"
                 className="min-h-[48px] rounded-xl border border-gray-300 px-3 text-base" />
          <button type="button"
                  disabled={ocupado || !aQuien || !motivo.trim()}
                  onClick={() => accion(async () => {
                    await api.post(`/tickets/${id}/asignar`, { id_usuario: Number(aQuien), motivo: motivo.trim() });
                    setPanel(''); setMotivo(''); setAQuien('');
                  })}
                  className="min-h-[48px] rounded-xl bg-[var(--color-marca)] px-4 font-semibold text-white disabled:opacity-50">
            Asignar
          </button>
        </div>
      )}

      {panel === 'espera' && (
        <div className="flex flex-col gap-2 rounded-xl border border-gray-200 p-3">
          <p className="text-sm text-gray-700">El reloj se detiene mientras esperas. Di qué esperas:</p>
          <select value={espera} onChange={(e) => setEspera(e.target.value)}
                  className="min-h-[48px] rounded-xl border border-gray-300 px-3 text-base">
            <option value="SOLICITANTE">A quien reportó</option>
            <option value="PROVEEDOR">A un proveedor</option>
            <option value="PIEZA">A una pieza</option>
          </select>
          <button type="button" disabled={ocupado}
                  onClick={() => accion(async () => {
                    await api.post(`/tickets/${id}/eventos`, {
                      mensaje: 'En espera', nuevo_estado: 'EN_ESPERA', motivo_espera: espera,
                    });
                    setPanel('');
                  })}
                  className="min-h-[48px] rounded-xl bg-amber-500 px-4 font-semibold text-white disabled:opacity-50">
            Poner en espera
          </button>
        </div>
      )}

      {panel === 'resolver' && (
        <div className="flex flex-col gap-2 rounded-xl border border-gray-200 p-3">
          <Ayuda id="mesa.resolver" />
          <textarea value={solucion} onChange={(e) => setSolucion(e.target.value)} rows={2}
                    placeholder="Qué hiciste para resolverlo"
                    className="rounded-xl border border-gray-300 p-3 text-base" />
          <input value={causa} onChange={(e) => setCausa(e.target.value)}
                 placeholder="Causa (opcional)"
                 className="min-h-[48px] rounded-xl border border-gray-300 px-3 text-base" />
          <input value={tiempo} onChange={(e) => setTiempo(e.target.value)}
                 inputMode="numeric" placeholder="Minutos que te tomó (opcional)"
                 className="min-h-[48px] rounded-xl border border-gray-300 px-3 text-base" />
          <button type="button" disabled={ocupado || !solucion.trim()}
                  onClick={() => accion(async () => {
                    await api.post(`/tickets/${id}/resolver`, {
                      solucion: solucion.trim(),
                      causa: causa.trim() || null,
                      tiempo_min: tiempo ? Number(tiempo) : null,
                    });
                    setPanel('');
                  })}
                  className="min-h-[48px] rounded-xl bg-green-600 px-4 font-semibold text-white disabled:opacity-50">
            Marcar resuelto
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// Guardar la solución en la base de conocimiento · DIRECTRIZ-TICKETS F7
// ---------------------------------------------------------------------
// Cuando el ticket ya está resuelto, quien atiende puede pasar su solución a
// la base de conocimiento de la empresa con un clic. Se prellena en el
// servidor desde el ticket (título, problema, solución, categoría); aquí
// solo se dispara y se confirma visiblemente que quedó guardado.
function GuardarComoArticulo({ id }) {
  const [estado, setEstado] = useState('');   // '', 'guardando', 'guardado', 'error'
  const [error, setError] = useState('');

  async function guardar() {
    if (estado === 'guardando') return;
    setEstado('guardando');
    setError('');
    try {
      await api.post(`/tickets/${id}/articulo`, {});
      setEstado('guardado');
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo guardar el artículo.');
      setEstado('error');
    }
  }

  if (estado === 'guardado') {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-green-200 bg-green-50 p-3 text-sm text-green-900">
        <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
        Solución guardada en la base de conocimiento. La próxima vez que pase, ahí estará.
      </div>
    );
  }

  return (
    <div>
      {error && <p role="alert" className="mb-2 rounded-lg bg-red-50 p-3 text-sm text-red-800">{error}</p>}
      <button
        type="button" onClick={guardar} disabled={estado === 'guardando'}
        className="flex min-h-[48px] items-center justify-center gap-2 self-start
                   rounded-xl border border-[var(--color-marca)] px-4 font-semibold
                   text-[var(--color-marca)] hover:bg-blue-50 disabled:opacity-50"
      >
        {estado === 'guardando'
          ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
          : <BookOpen className="h-5 w-5" aria-hidden="true" />}
        Guardar como artículo
      </button>
      <Ayuda id="conocimiento.convertir" />
    </div>
  );
}
